import React, {
  useEffect,
  createContext,
  useState,
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';

import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';
import { filterListBy } from 'src/utils/helpers/filter';

const initialState = {
  myApps: null,
  filteredMyApps: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { industry: '', search_title: '' },
  setFilterValues: () => Promise.resolve(),
  saveMyApp: () => Promise.resolve(),
  deleteMyApp: () => Promise.resolve(),
  updateMyApp: () => Promise.resolve(),
  updateAppLocal: () => {}
};

const MyAppsContext = createContext(initialState);

export const MyAppsProvider = (props) => {
  const { children, ...contextProviderProps } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [activity, setActivity] = useState(null);
  const [isPreloaded, setPreloaded] = useState(false);
  const [myApps, setMyApps] = useState(initialState.myApps);
  const [filteredMyApps, setFilteredMyApps] = useState(
    initialState.filteredMyApps
  );
  const client = useRestApiClient();

  const deleteMyApp = async (appsId) => {
    setActivity('deleting');
    await client.delete(`/features/${appsId}`);
    setMyApps((prev) => {
      const filtered = prev.filter((row) => row.id !== appsId);
      return [...filtered];
    });
    setActivity(null);
  };

  const saveMyApp = async (payload) => {
    try {
      setActivity('saving');
      const {
        data: { message: newApp }
      } = await client.post('/features', payload);

      setMyApps((currentList) => {
        return currentList ? [...currentList, newApp] : [newApp];
      });
      setPreloaded(true);
      setActivity(null);
    } catch (error) {
      setActivity(null);
      throw error;
    }
  };

  const updateMyApp = useCallback(
    async (appsId, payload, isLocal = false) => {
      // update local copy of record
      if (isLocal) {
        setMyApps((currentList) => {
          const newList = currentList.map((app) => {
            return app.id === appsId ? { ...app, ...payload } : app;
          });
          return [...newList];
        });
      } else {
        let result = {};
        try {
          setActivity('updating');
          const {
            data: { message }
          } = await client.patch(`/features/${appsId}`, payload);

          if (message.pending_approval) {
            delete payload.private;
          }

          result = message;
        } catch (error) {
          setActivity(null);
          throw error;
        }
        setActivity(null);

        return result;
      }

      return null;
    },
    [myApps]
  );

  const updateAppLocal = useCallback(
    (appId, data) => {
      setMyApps((currentList) => {
        const newList = currentList.map((app) => {
          return app.id === appId ? { ...app, ...data } : app;
        });
        return [...newList];
      });
    },
    [myApps]
  );

  const loadMyApps = async (force = false) => {
    if (myApps?.length > 0 && !force) return;
    if (activity === 'fetching') return;

    try {
      setActivity('fetching');
      const {
        data: { message }
      } = await client.get('/collections/features');
      setMyApps(message);
      // setFilteredMyApps(message);
      setPreloaded(true);
    } catch (error) {
      setMyApps([]);
      if (error?.response?.status === 404) {
        setPreloaded(true);
      }
      throw error;
    } finally {
      setActivity(null);
    }
  };

  useEffect(() => {
    let filterResult = myApps || [];
    const { industry, search_title: searchTitle } = filterValues;
    filterResult = filterListBy(filterResult, 'industry', industry);

    if (searchTitle) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      filterResult = filterResult.filter(
        ({ description, title }) =>
          (description &&
            description.toString().toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toString().toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredMyApps([...filterResult]);
  }, [myApps, filterValues]);

  const values = useMemo(
    () => ({
      isPreloaded,
      activity,
      filterValues,
      myApps,
      filteredMyApps,
      setFilterValues,
      loadMyApps,
      saveMyApp,
      updateMyApp,
      deleteMyApp,
      updateAppLocal
    }),
    [isPreloaded, activity, filterValues, myApps, filteredMyApps, updateMyApp]
  );

  return (
    <MyAppsContext.Provider value={values} {...contextProviderProps}>
      {children}
    </MyAppsContext.Provider>
  );
};

MyAppsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default MyAppsContext;
